import { forwardRef, useEffect, type ForwardedRef } from "react";
import { useRouter } from "next/navigation";
import { cn } from "~/utils/cn";

const SearchInput = (
  {
    setSearchOpen,
    isSearchOpen,
  }: {
    setSearchOpen: (value: boolean) => void;
    isSearchOpen: boolean;
  },
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const router = useRouter();

  function handleClose() {
    setSearchOpen(!isSearchOpen);
    if (typeof ref === "object" && ref?.current) {
      ref.current.value = "";
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e?.key === "Enter") {
      //Navigate to search page
      void router.push(`/search?term=${(e.target as HTMLInputElement).value}`);
    }
  }

  function handleClick() {
    if (typeof ref === "object" && ref?.current) {
      void router.push(`/search?term=${ref.current.value}`);
    }
  }

  useEffect(() => {
    if (typeof ref === "object" && ref?.current) {
      ref.current.focus();
    }
  }, [isSearchOpen, ref]);

  return (
    <div
      className={cn(
        "absolute left-0 top-0 block h-full w-full bg-black px-15 lg:bg-transparent lg:px-24 xl:px-48",
        isSearchOpen ? "" : "-z-10",
      )}
    >
      {/* Search bar with overlay */}
      <div
        className={cn([
          "z-100 group left-0 top-0 h-full w-full transition-all",
          isSearchOpen ? "opacity-1" : "opacity-0 delay-700",
        ])}
      >
        <div className="left-0 top-0 mx-auto flex h-full w-full max-w-1480 items-center justify-center gap-x-2 overflow-hidden pl-5 sm:justify-end md:pl-10">
          <button
            className={cn([
              "z-50 flex w-10 items-center justify-center stroke-white p-2 transition-all delay-150",
              isSearchOpen
                ? "opacity-1 translate-x-0"
                : "translate-x-96 opacity-0",
            ])}
            onClick={() => handleClick()}
            aria-label="Open searchbar"
          >
            <span className="material-icons-round text-white">search</span>
          </button>
          <form
            method="get"
            action="/search"
            className="block w-full max-w-xs md:max-w-88 lg:max-w-sm"
          >
            <input
              ref={ref}
              type="text"
              placeholder="Search here"
              className={cn([
                "peer w-full border border-white/60 bg-transparent px-4 py-2 text-white outline-none transition-all marker:text-white placeholder:text-white/60 group-hover:border-white",
                isSearchOpen ? "opacity-1 delay-300" : "opacity-0",
              ])}
              name="term"
              onKeyDown={handleKeyDown}
            />
          </form>
          <button
            onClick={handleClose}
            className={cn([
              "group flex h-10 w-10 items-center justify-center transition-all",
              isSearchOpen ? "opacity-1" : "opacity-0 delay-150",
            ])}
            aria-label="Close searchbar"
          >
            <span className="material-icons-round text-white">close</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default forwardRef(SearchInput);
